import { PALLET_STATUSES, RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { lastStoredLocation } from './ManifestGrid/utils';
import { inPlaceReceiveEvents, receivedEventIds, receivesInPlace, unreceivedMarkers } from './utils';
import { toArrayLiteral } from '@/utilities/toArrayLiteral';
import { post } from '@/utilities/onwardClient';
import { TRIGGER_ORDER_EVENT } from '@/constants/apiRoutes';
import { COMPLETION_SOURCES } from '@/constants/completionSources';
import { restoreDashPattern } from 'pdf-lib';
import { useCallback } from 'react';
import { receive } from '@onward-delivery/core';
import {
    manifestItemsOnPallets,
    ordersCompletedByReceive,
    ordersOnPallet,
    singlePalletAtReceived,
} from '@/utilities/manifestPallets';
import { findMatchingWarehouseEvent } from '@/utilities/warehouseEvents';
import { buildTrackingEvents } from '@/utilities/trackingEvents';

/**
 * Warehouse mutations address pallets; `tracking_events` rows are per item. No `returnEventType` — a
 * return moves through a warehouse like a forward item. No `skipExisting` — these actions are
 * legitimately repeatable, so `tracking_events_uniq` is the right guard.
 */
const palletTrackingEvents = (manifest, pallets, eventType) => {
    const palletIds = (Array.isArray(pallets) ? pallets : [pallets])
        .map((pallet) => pallet?.pallet_id ?? pallet)
        .filter(Boolean);

    return buildTrackingEvents({
        items: manifestItemsOnPallets(manifest?.orders, palletIds),
        eventType,
    });
};

const buildReceiveUpsertEvents = (order, received_date) => {
    if (receivesInPlace(order)) return inPlaceReceiveEvents(order, received_date);

    if ((order.wh_events || []).some((e) => e.action === 'START:PICKING_UP')) return [];

    // Re-receiving after an un-receive: restore the existing row, since advance() has nowhere left to
    // travel from RECEIVING. The upsert targets it by event_id, so this patches rather than duplicates.
    const unreceived = unreceivedMarkers(order);

    let baseEvents = [];
    if (unreceived.length) {
        baseEvents = unreceived.map((event) => ({ ...event, status: 'RECEIVED', received_date }));
    } else {
        baseEvents = receive(order, {}, { received_date }) || [];
    }

    return (baseEvents || []).filter(Boolean).map((event) => {
        const existing = findMatchingWarehouseEvent(order.wh_events, { action: event.action });
        const merged = existing?.event_id ? { ...existing, ...event } : event;
        const { __typename, state, transition, driver, wh_driver, location, ...remaining } = merged;
        return {
            ...remaining,
            order_id: remaining.order_id || order.order_id,
        };
    });
};

export const useManifestCallbacks = (state, callbacks) => {
    // `completesOrder: false` suppresses the order-level receive events — used by partial receive,
    // where the split leaves a not-received remainder that isn't in this order snapshot yet.
    // `completesOrderIds` comes from the grid's bulk plan; without it the order is judged alone.
    const receiveItem = useCallback((item, manifest, { completesOrder = true, completesOrderIds } = {}) => {
        let events;
        const completed = completesOrder
            ? ordersCompletedByReceive([item.order], {
                  completesOrderIds,
                  batch: { itemIds: [item.item_id] },
              })
            : [];
        if (completed.length) {
            events = buildReceiveUpsertEvents(item.order, new Date().toISOString());
        }

        callbacks.insertPallet({
            variables: {
                item_id: item.item_id,
                pallet: singlePalletAtReceived(manifest.warehouse_id),
                ...(events?.length ? { events } : {}),
                // Received only — web leaves warehouse_location null, so nothing is stored here.
                trackingEvents: buildTrackingEvents({
                    items: [item],
                    eventType: 'ITEM_RECEIVED_INTO_WAREHOUSE',
                }),
            },
        });
    }, []);

    const receivePallet = useCallback(
        (item, pallet, manifest, { completesOrderIds } = {}) => {
            const received_date = new Date().toISOString();

            const onPallet = ordersOnPallet(state.manifest?.orders, pallet.pallet_id);
            const orders = onPallet.length ? onPallet : [item.order].filter(Boolean);
            const events = ordersCompletedByReceive(orders, {
                completesOrderIds,
                batch: { palletIds: [pallet.pallet_id] },
            }).flatMap((order) => buildReceiveUpsertEvents(order, received_date));

            callbacks.updatePallet({
                variables: {
                    pallet_id: pallet.pallet_id,
                    update: {
                        warehouse_id: manifest.warehouse_id,
                        warehouse_location: null,
                        warehouse_status: PALLET_STATUSES.RECEIVED,
                    },
                    ...(events.length ? { events } : {}),
                    // A pallet can span orders, so this covers every item on it, not just `item`.
                    trackingEvents: palletTrackingEvents(state.manifest, [pallet], 'ITEM_RECEIVED_INTO_WAREHOUSE'),
                },
            });
        },
        [state.manifest]
    );

    const preconsolidateItems = useCallback((items, manifest, warehouseStatus = 'NOT_RECEIVED') => {
        callbacks.insertEmptyPallet({
            variables: {
                object: {
                    type: 'CONSOLIDATED',
                    warehouse_id: manifest.warehouse_id,
                    warehouse_location: null,
                    warehouse_status: warehouseStatus,
                    pallet_name: `${items[0].order.order_number} consolidated label`,
                },
            },
            onCompleted: ({ insert_pallet_items_one }) => {
                const itemUpdates = items.map((item) => ({
                    where: { item_id: { _eq: item.item_id } },
                    _set: { pallet_id: insert_pallet_items_one.pallet_id },
                }));

                const orderIds = [...new Set(items.map((item) => item.order_id).filter(Boolean))];

                callbacks
                    .updateItemPallets({
                        variables: {
                            itemUpdates: itemUpdates,
                            palletUpdates: [],
                            palletsToDelete: [],
                        },
                    })
                    .then(() => {
                        // Consolidation changed the pallet layout, re-price the affected orders
                        if (orderIds.length) {
                            post(TRIGGER_ORDER_EVENT, {
                                order_ids: orderIds,
                                event: 'globals:price_order',
                            }).catch((e) => console.error('Failed to trigger reprice after consolidation', e));
                        }
                    });
            },
        });
    }, []);

    const renamePallet = useCallback((pallets, newName) => {
        return callbacks.updatePallets({
            variables: {
                updates: pallets.map((pallet) => ({
                    where: { pallet_id: { _eq: pallet.pallet_id } },
                    _set: {
                        pallet_name: newName,
                    },
                })),
            },
            onError: (error) => {
                callbacks.onError(`Failed to rename pallet`)(error);
            },
        });
    }, []);

    // Two physical actions share this write — a first put-away is a store, moving an already-stored
    // pallet is a relocate — and both end at warehouse_status STORED. The caller says which.
    const storePallets = useCallback(
        (pallets, { eventType = 'ITEM_STORED_IN_WAREHOUSE' } = {}) => {
            return callbacks.updatePallets({
                variables: {
                    updates: pallets.map((pallet) => ({
                        where: { pallet_id: { _eq: pallet.pallet_id } },
                        _set: {
                            warehouse_location: pallet.warehouse_location,
                            warehouse_status: PALLET_STATUSES.STORED,
                            is_pool: pallet.is_pool,
                        },
                    })),
                    trackingEvents: palletTrackingEvents(state.manifest, pallets, eventType),
                },
                onError: (error) => {
                    if (error.message.includes('pallet_locations_unique')) {
                        callbacks.setNotification({ severity: 'error', message: 'Bin location is already occupied' });
                    } else {
                        callbacks.onError(
                            `Failed to store ${pallets.some((pallet) => pallet.type === 'SINGLE') ? 'item' : 'pallet'}`
                        )(error);
                    }
                },
            });
        },
        [state.manifest]
    );

    const saveStagingZone = useCallback((manifest, stagingZone) => {
        return callbacks.updateManifest({
            variables: {
                manifest_id: manifest.manifest_id,
                update: {
                    staging_zone: stagingZone,
                },
            },
            onError: (error) => {
                callbacks.onError(`Failed to set staging zone`)(error);
            },
        });
    }, []);

    // Consolidate any selection — items across orders — onto one new pallet. Old pallets are deleted
    // only once nothing is left on them (checked against every item on the pallet, not just this
    // manifest's), and pool pallets are never deleted since they're shared by design.
    const consolidateItems = useCallback(async (items, manifest) => {
        const unique = [...new Map((items || []).map((item) => [item.item_id, item])).values()];
        if (unique.length < 2) return null;

        const selectedIds = new Set(unique.map((item) => item.item_id));
        const palletById = new Map(
            unique.filter((item) => item?.pallet?.pallet_id).map((item) => [item.pallet.pallet_id, item.pallet])
        );

        let palletsToDelete = [];
        if (palletById.size) {
            const { data } = await callbacks.getPalletItems({ variables: { pallet_ids: [...palletById.keys()] } });
            const stillOccupied = new Set(
                (data?.items || []).filter(({ item_id }) => !selectedIds.has(item_id)).map(({ pallet_id }) => pallet_id)
            );
            palletsToDelete = [...palletById.keys()].filter(
                (palletId) => !stillOccupied.has(palletId) && !palletById.get(palletId)?.is_pool
            );
        }

        // Preserve received status: consolidating already-received items shouldn't reset them. The shared
        // pallet carries one status, so a mixed selection can't be represented without silently promoting
        // or demoting a group — refuse it rather than pick a side (the grid also hides the button).
        const received = unique.filter((item) => RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status));
        if (received.length && received.length !== unique.length) {
            callbacks.setNotification({
                severity: 'error',
                message: 'Consolidate received items separately from not-received items.',
            });
            return null;
        }
        const allReceived = received.length === unique.length;
        const orderNumbers = [...new Set(unique.map((item) => item.order?.order_number).filter(Boolean))];
        const palletName =
            orderNumbers.length === 1
                ? `${orderNumbers[0]} consolidated label`
                : `Consolidated label (${orderNumbers.length} orders)`;

        const created = await callbacks.insertEmptyPallet({
            variables: {
                object: {
                    type: 'CONSOLIDATED',
                    warehouse_id: manifest.warehouse_id,
                    warehouse_location: null,
                    warehouse_status: allReceived ? PALLET_STATUSES.RECEIVED : 'NOT_RECEIVED',
                    pallet_name: palletName,
                },
            },
        });

        const pallet_id = created?.data?.insert_pallet_items_one?.pallet_id;
        if (!pallet_id) return null;

        const res = await callbacks.updateItemPallets({
            variables: {
                itemUpdates: unique.map((item) => ({
                    where: { item_id: { _eq: item.item_id } },
                    _set: { pallet_id },
                })),
                palletUpdates: [],
                palletsToDelete,
            },
        });

        const orderIds = [...new Set(unique.map((item) => item.order_id).filter(Boolean))];
        if (orderIds.length) {
            post(TRIGGER_ORDER_EVENT, { order_ids: orderIds, event: 'globals:price_order' }).catch((e) =>
                console.error('Failed to trigger reprice after consolidation', e)
            );
        }
        return res;
    }, []);

    const consolidatePallets = useCallback(
        (newPallets) => {
            const itemUpdates = [];
            const palletUpdates = [];
            const palletsToDelete = [];
            // Track which orders had pallet assignments change, to re-price them after.
            const ordersTouched = new Set();
            newPallets.forEach((pallet) => {
                if (pallet.items.length === 0) {
                    palletsToDelete.push(pallet.pallet_id);
                } else {
                    const isConsolidated = pallet.items.length > 1;
                    pallet.items.forEach((item) => {
                        itemUpdates.push({
                            where: { item_id: { _eq: item.item_id } },
                            _set: { pallet_id: pallet.pallet_id },
                        });
                        const orderId = item.order_id;
                        if (orderId) ordersTouched.add(orderId);
                    });
                    palletUpdates.push({
                        where: { pallet_id: { _eq: pallet.pallet_id } },
                        _set: {
                            type: isConsolidated ? 'CONSOLIDATED' : 'SINGLE',
                            warehouse_status: 'RECEIVED',
                            warehouse_location: null,
                        },
                    });
                }
            });

            return callbacks
                .updateItemPallets({
                    variables: {
                        itemUpdates: itemUpdates,
                        palletUpdates: palletUpdates,
                        palletsToDelete: palletsToDelete,
                    },
                })
                .then((res) => {
                    // Consolidation changed the pallet layout, re-price the affected orders
                    if (ordersTouched.size) {
                        post(TRIGGER_ORDER_EVENT, {
                            order_ids: [...ordersTouched],
                            event: 'globals:price_order',
                        }).catch((e) => console.error('Failed to trigger reprice after consolidation', e));
                    }
                    return res;
                });
        },
        [state.manifest]
    );

    const stagePallets = useCallback(
        (pallets) => {
            const palletIds = pallets.map((pallet) => pallet?.pallet_id).filter(Boolean);
            return callbacks.updatePallets({
                variables: {
                    updates: [
                        {
                            where: { pallet_id: { _in: palletIds } },
                            _set: {
                                warehouse_location: null,
                                warehouse_status: PALLET_STATUSES.STAGED,
                            },
                        },
                    ],
                    // Staged only. pickStage collapses pick and stage, but the items are still on
                    // site — a pick event here would drop them out of the on-hand count early.
                    trackingEvents: palletTrackingEvents(state.manifest, pallets, 'ITEM_STAGED'),
                },
                onError: callbacks.onError('Failed to stage pallets'),
            });
        },
        [state.manifest]
    );

    const unreceiveOrder = useCallback(({ orders = [], pallets = [] }) => {
        const palletIds = pallets.map((pallet) => pallet?.pallet_id).filter(Boolean);
        const event_ids = receivedEventIds(orders);

        return callbacks
            .updatePallets({
                variables: {
                    updates: [
                        {
                            where: { pallet_id: { _in: palletIds } },
                            _set: {
                                warehouse_location: null,
                                warehouse_status: PALLET_STATUSES.NOT_RECEIVED,
                            },
                        },
                    ],
                },
                onError: callbacks.onError('Failed to un-receive order'),
            })
            .then((res) => {
                // onError makes Apollo resolve rather than reject — don't roll back OMS after a failed
                // pallet write.
                if (res?.errors?.length) return null;

                return event_ids.length ? callbacks.rollbackReceiveEvents({ variables: { event_ids } }) : null;
            });
    }, []);

    // Back to RECEIVED (no bin), or STORED at each pallet's last logged bin. Pallets with no bin in
    // their history fall back to RECEIVED rather than being written STORED with no location.
    const unstagePallets = useCallback((pallets, mode) => {
        const restoring = mode === 'STORED';
        const withLocation = restoring ? pallets.filter((pallet) => lastStoredLocation(pallet)) : [];
        const withoutLocation = pallets.filter((pallet) => !withLocation.includes(pallet));

        const updates = [
            ...withLocation.map((pallet) => ({
                where: { pallet_id: { _eq: pallet.pallet_id } },
                _set: {
                    warehouse_location: lastStoredLocation(pallet),
                    warehouse_status: PALLET_STATUSES.STORED,
                },
            })),
            ...(withoutLocation.length
                ? [
                      {
                          where: { pallet_id: { _in: withoutLocation.map((pallet) => pallet.pallet_id) } },
                          _set: {
                              warehouse_location: null,
                              warehouse_status: PALLET_STATUSES.RECEIVED,
                          },
                      },
                  ]
                : []),
        ];

        return callbacks.updatePallets({
            variables: { updates },
            onError: (error) => {
                if (error.message.includes('pallet_locations_unique')) {
                    callbacks.setNotification({
                        severity: 'error',
                        message: 'That bin is already occupied — un-stage to Received instead',
                    });
                } else {
                    callbacks.onError('Failed to un-stage order')(error);
                }
            },
        });
    }, []);

    const pickupPallets = useCallback(
        (pallets) => {
            const palletIds = pallets.map((pallet) => pallet?.pallet_id).filter(Boolean);
            return callbacks.updatePallets({
                variables: {
                    updates: [
                        {
                            where: { pallet_id: { _in: palletIds } },
                            _set: {
                                warehouse_location: null,
                                warehouse_status: PALLET_STATUSES.PICKED_UP,
                            },
                        },
                    ],
                    trackingEvents: palletTrackingEvents(state.manifest, pallets, 'ITEM_DELIVERED'),
                },
                onError: callbacks.onError('Failed to pick up pallets'),
            });
        },
        [state.manifest]
    );

    const reportException = useCallback(({ exception, items }) => {
        const { __typename, items: existingItems, order, images, ...rest } = exception;

        if (exception.exception_id) {
            callbacks.updateException({
                variables: {
                    exception_id: exception.exception_id,
                    exception: {
                        ...rest,
                        images: toArrayLiteral(images || []),
                    },
                },
            });
        } else {
            callbacks.insertException({
                variables: {
                    exceptions: items.map((item) => ({
                        order_id: item.order_id,
                        item_id: item.item_id,
                        exception: {
                            data: { ...rest, images: toArrayLiteral(images || []) },
                        },
                    })),
                },
            });
        }
    }, []);

    const completeOrders = useCallback((orders) => {
        return callbacks.updateOrders({
            variables: {
                order_ids: orders.map((o) => o.order_id),
                update: {
                    completion_time: new Date().toISOString(),
                    order_status: 'complete',
                    completion_source: COMPLETION_SOURCES.WEB,
                },
            },
            onError: callbacks.onError('Failed to complete orders'),
        });
    }, []);

    return {
        receiveItem,
        preconsolidateItems,
        receivePallet,
        storePallets,
        saveStagingZone,
        consolidateItems,
        consolidatePallets,
        stagePallets,
        unstagePallets,
        unreceiveOrder,
        pickupPallets,
        reportException,
        completeOrders,
        renamePallet,
    };
};
